import { fabric } from 'fabric';

/**
 * Shared pointer / zoom utilities for pinch-to-zoom on fabric canvases and
 * CSS-transform based image viewers. Kept framework-agnostic on purpose.
 */

export interface Point2D {
  x: number;
  y: number;
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export function distance(a: Point2D, b: Point2D): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function midpoint(a: Point2D, b: Point2D): Point2D {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export interface FabricPinchZoomOptions {
  /** Minimum zoom factor. Defaults to 1 (no zoom-out beyond fit). */
  minZoom?: number;
  /** Maximum zoom factor. Defaults to 6. */
  maxZoom?: number;
  /** Zoom factor used when the user double-taps from min zoom. Defaults to 2.5. */
  doubleTapZoom?: number;
  /**
   * Enable mouse-wheel / trackpad zoom. Defaults to true.
   * Uses the same `viewportTransform` as pinch so both gestures compose.
   */
  enableWheelZoom?: boolean;
  /**
   * Return true to suppress wheel zoom (e.g. while the user is drawing a
   * polygon). Pinch zoom is unaffected — it already resets to fit on drag-end.
   */
  isDrawing?: () => boolean;
  /** Called when a two-finger gesture starts. Use to cancel in-progress drawing. */
  onPinchStart?: () => void;
  /** Called when the gesture ends. Use to re-select active object. */
  onPinchEnd?: () => void;
}

export interface FabricPinchZoomHandle {
  detach(): void;
  isPinching(): boolean;
}

/**
 * Attach two-finger pinch-zoom + pan to a fabric.Canvas via Pointer Events.
 *
 * Implementation notes:
 * - Operates on `canvas.viewportTransform` only — never resizes the DOM canvas.
 *   Therefore it composes cleanly with whatever code is drawing into the canvas.
 * - Listens on `upperCanvasEl` so it sits on the same surface fabric uses for
 *   its own touch/mouse handling.
 * - Only reacts to `pointerType === 'touch'` to avoid intercepting mouse/pen.
 * - During a pinch we set `selection = false` and `skipTargetFind = true` so
 *   fabric does not try to drag the active object simultaneously.
 */
export function attachFabricPinchZoom(
  canvas: fabric.Canvas,
  options: FabricPinchZoomOptions = {}
): FabricPinchZoomHandle {
  const minZoom = options.minZoom ?? 1;
  const maxZoom = options.maxZoom ?? 6;
  const upperEl = (canvas as unknown as { upperCanvasEl?: HTMLCanvasElement }).upperCanvasEl;

  if (!upperEl) {
    return { detach: () => undefined, isPinching: () => false };
  }

  const activePointers = new Map<number, Point2D>();
  let pinchStartDistance = 0;
  let pinchStartZoom = 1;
  let pinchLastCenter: Point2D | null = null;
  let pinching = false;

  let panStartClient: Point2D | null = null;
  let panStartVpt: number[] | null = null;

  const previousSelection = canvas.selection;

  const toCanvasPoint = (client: Point2D): Point2D => {
    const rect = upperEl.getBoundingClientRect();
    return { x: client.x - rect.left, y: client.y - rect.top };
  };

  const doubleTapZoom = options.doubleTapZoom ?? 2.5;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_RADIUS_PX = 30;
  let lastTapAt = 0;
  let lastTapPoint: Point2D | null = null;

  const maybeHandleDoubleTap = (e: PointerEvent): void => {
    const now = performance.now();
    const tap: Point2D = { x: e.clientX, y: e.clientY };
    if (lastTapPoint && now - lastTapAt < DOUBLE_TAP_MS && distance(tap, lastTapPoint) < DOUBLE_TAP_RADIUS_PX) {
      const canvasPt = toCanvasPoint(tap);
      const fabricPt = new fabric.Point(canvasPt.x, canvasPt.y);
      if (canvas.getZoom() > minZoom + 1e-3) {
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      } else {
        canvas.zoomToPoint(fabricPt, doubleTapZoom);
        clampViewport();
      }
      canvas.requestRenderAll();
      lastTapAt = 0;
      lastTapPoint = null;
      return;
    }
    lastTapAt = now;
    lastTapPoint = tap;
  };

  const clampViewport = (): void => {
    const vpt = canvas.viewportTransform;
    if (!vpt) return;
    const zoom = canvas.getZoom();
    const w = canvas.getWidth();
    const h = canvas.getHeight();
    if (zoom <= minZoom + 1e-3) {
      vpt[4] = 0;
      vpt[5] = 0;
    } else {
      const maxOffsetX = w * (zoom - 1);
      const maxOffsetY = h * (zoom - 1);
      vpt[4] = clamp(vpt[4], -maxOffsetX, 0);
      vpt[5] = clamp(vpt[5], -maxOffsetY, 0);
    }
    canvas.setViewportTransform(vpt);
    canvas.calcOffset();
  };

  const beginPinch = (): void => {
    const [a, b] = [...activePointers.values()];
    pinchStartDistance = distance(a, b) || 1;
    pinchStartZoom = canvas.getZoom();
    pinchLastCenter = toCanvasPoint(midpoint(a, b));
    pinching = true;

    canvas.discardActiveObject();
    canvas.selection = false;
    canvas.skipTargetFind = true;
    canvas.requestRenderAll();
    options.onPinchStart?.();
  };

  const updatePinch = (): void => {
    const pts = [...activePointers.values()];
    if (pts.length !== 2 || !pinchLastCenter) return;
    const [a, b] = pts;
    const dist = distance(a, b) || 1;
    const ratio = dist / pinchStartDistance;
    const zoom = clamp(pinchStartZoom * ratio, minZoom, maxZoom);
    const center = toCanvasPoint(midpoint(a, b));

    canvas.zoomToPoint(new fabric.Point(center.x, center.y), zoom);

    // Pan along with the moving pinch midpoint for natural one-gesture behaviour.
    const vpt = canvas.viewportTransform;
    if (vpt) {
      vpt[4] += center.x - pinchLastCenter.x;
      vpt[5] += center.y - pinchLastCenter.y;
      canvas.setViewportTransform(vpt);
    }
    pinchLastCenter = center;
    clampViewport();
  };

  const endPinch = (): void => {
    if (!pinching) return;
    pinching = false;
    pinchLastCenter = null;
    canvas.selection = previousSelection;
    canvas.skipTargetFind = false;
    canvas.requestRenderAll();
    options.onPinchEnd?.();
  };

  const onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.size === 1) {
      maybeHandleDoubleTap(e);
      if (canvas.getZoom() > minZoom + 1e-3 && !options.isDrawing?.()) {
        panStartClient = { x: e.clientX, y: e.clientY };
        const vpt = canvas.viewportTransform;
        panStartVpt = vpt ? [...vpt] : null;
      }
    }
    if (activePointers.size === 2) {
      beginPinch();
      panStartClient = null;
      panStartVpt = null;
    }
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch' || !activePointers.has(e.pointerId)) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinching && activePointers.size === 2) {
      updatePinch();
      e.preventDefault();
      return;
    }
    if (activePointers.size === 1 && panStartClient && panStartVpt && !options.isDrawing?.()) {
      const dx = e.clientX - panStartClient.x;
      const dy = e.clientY - panStartClient.y;
      const vpt = canvas.viewportTransform;
      if (vpt) {
        vpt[4] = panStartVpt[4] + dx;
        vpt[5] = panStartVpt[5] + dy;
        canvas.setViewportTransform(vpt);
        clampViewport();
        canvas.requestRenderAll();
      }
      e.preventDefault();
    }
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;
    activePointers.delete(e.pointerId);
    if (activePointers.size < 2) {
      endPinch();
    }
    if (activePointers.size === 0) {
      panStartClient = null;
      panStartVpt = null;
    }
  };

  const onDblClick = (e: MouseEvent): void => {
    const canvasPt = toCanvasPoint({ x: e.clientX, y: e.clientY });
    const fabricPt = new fabric.Point(canvasPt.x, canvasPt.y);
    if (canvas.getZoom() > minZoom + 1e-3) {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    } else {
      canvas.zoomToPoint(fabricPt, doubleTapZoom);
      clampViewport();
    }
    canvas.requestRenderAll();
    lastTapAt = 0;
    lastTapPoint = null;
  };

  upperEl.addEventListener('pointerdown', onPointerDown);
  upperEl.addEventListener('pointermove', onPointerMove);
  upperEl.addEventListener('pointerup', onPointerUp);
  upperEl.addEventListener('pointercancel', onPointerUp);
  upperEl.addEventListener('pointerleave', onPointerUp);
  upperEl.addEventListener('dblclick', onDblClick);

  // ---- Wheel zoom -----------------------------------------------------------
  const enableWheelZoom = options.enableWheelZoom !== false;
  const onWheel = (evt: WheelEvent): void => {
    if (options.isDrawing?.()) return;
    const zoom = clamp(canvas.getZoom() * 0.999 ** evt.deltaY, minZoom, maxZoom);
    canvas.zoomToPoint(new fabric.Point(evt.offsetX, evt.offsetY), zoom);
    clampViewport();
    canvas.requestRenderAll();
    evt.preventDefault();
    evt.stopPropagation();
  };
  if (enableWheelZoom) {
    upperEl.addEventListener('wheel', onWheel, { passive: false });
  }

  return {
    detach: (): void => {
      upperEl.removeEventListener('pointerdown', onPointerDown);
      upperEl.removeEventListener('pointermove', onPointerMove);
      upperEl.removeEventListener('pointerup', onPointerUp);
      upperEl.removeEventListener('pointercancel', onPointerUp);
      upperEl.removeEventListener('pointerleave', onPointerUp);
      upperEl.removeEventListener('dblclick', onDblClick);
      if (enableWheelZoom) {
        upperEl.removeEventListener('wheel', onWheel);
      }
      activePointers.clear();
    },
    isPinching: (): boolean => pinching,
  };
}
